/* eslint-disable @typescript-eslint/naming-convention, unicorn/prefer-event-target */
import {readId} from '@aeppic/document';
import type * as Types from '@aeppic/document';
import {EventEmitter} from '@aeppic/shared/event-emitter'; // eslint-disable-line n/file-extension-in-import
import {
	cloneQuery as cloneRuntimeQuery,
	isFieldMatchNode,
	isQuery,
	QueryMatcher,
	Scopes,
	toQuery as toRuntimeQuery,
} from '@aeppic/query';
import type {
	Condition,
	FieldMatchNode,
	MatchExpression,
	MatchOptions,
	Query,
	QueryBuilder,
	QueryParameterReference,
	QueryParameters,
} from '@aeppic/query';

export type AllSelection = {
	type: 'all';
};

export type NoneSelection = {
	type: 'none';
};

export type IdsSelection = {
	type: 'ids';
	ids: Types.DocumentId[];
};

export type QuerySelection = {
	type: 'query';
	query: Query;
};

export type UnionSelection = {
	type: 'union';
	selections: Selection[];
};

export type IntersectionSelection = {
	type: 'intersection';
	selections: Selection[];
};

export type DifferenceSelection = {
	type: 'difference';
	include: Selection;
	exclude: Selection;
};

export type Selection =
  AllSelection |
  NoneSelection |
  IdsSelection |
  QuerySelection |
  UnionSelection |
  IntersectionSelection |
  DifferenceSelection;

type MutableQueryParameters = unknown[];

export type SelectionLoweringContext = {
	resolver?: SelectionResolver;
};

export type SelectionResolver = {
	assertQuerySupported?(query: Query, context: SelectionLoweringContext): void;
	negateQuery?(query: Query, context: SelectionLoweringContext): Query;
};

export type SelectionChangedEvent = {
	selection: DocumentSelection;
	ids: Types.DocumentId[];
	added: Types.DocumentId[];
	removed: Types.DocumentId[];
};

export class UnsupportedSelectionFeatureError extends Error {
	feature: string;

	constructor(feature: string, message = `Selection feature ${feature} is not supported by query lowering v1.`) {
		super(message);
		this.name = 'UnsupportedSelectionFeatureError';
		this.feature = feature;
	}
}

export const Selections = {
	all,
	none,
	ids,
	query,
	union,
	intersection,
	difference,
	normalize: normalizeSelection,
	clone: cloneSelection,
	isSelection,
	toQuery,
	matches,
};

export function all(): AllSelection {
	return {type: 'all'};
}

export function none(): NoneSelection {
	return {type: 'none'};
}

export function ids(ids: Types.DocumentId | Types.DocumentId[]): IdsSelection {
	return {
		type: 'ids',
		ids: normalizeDocumentIds(ids),
	};
}

export function query(query: Query | QueryBuilder): QuerySelection {
	return {
		type: 'query',
		query: cloneRuntimeQuery(toRuntimeQuery(query)),
	};
}

export function union(...selections: Selection[]): UnionSelection {
	return normalizeSelection({
		type: 'union',
		selections,
	}) as UnionSelection;
}

export function intersection(...selections: Selection[]): IntersectionSelection {
	return normalizeSelection({
		type: 'intersection',
		selections,
	}) as IntersectionSelection;
}

export function difference(include: Selection, exclude: Selection): DifferenceSelection {
	return normalizeSelection({
		type: 'difference',
		include,
		exclude,
	}) as DifferenceSelection;
}

export function isSelection(value: unknown): value is Selection {
	try {
		normalizeSelection(value as Selection);
		return true;
	} catch {
		return false;
	}
}

export function cloneSelection(selection: Selection): Selection {
	return normalizeSelection(selection);
}

export function normalizeSelection(selection: Selection): Selection {
	if (!isObject(selection) || typeof selection.type !== 'string') {
		throw new Error('Selection must be an object with a type');
	}

	switch (selection.type) {
		case 'all': {
			return all();
		}

		case 'none': {
			return none();
		}

		case 'ids': {
			return ids(selection.ids);
		}

		case 'query': {
			if (!isQuery(selection.query)) {
				throw new Error('Selection query must be an @aeppic/query runtime query');
			}

			return query(selection.query);
		}

		case 'union': {
			assertSelectionArray(selection.selections, selection.type);
			return {
				type: 'union',
				selections: selection.selections.map(child => normalizeSelection(child)),
			};
		}

		case 'intersection': {
			assertSelectionArray(selection.selections, selection.type);
			return {
				type: 'intersection',
				selections: selection.selections.map(child => normalizeSelection(child)),
			};
		}

		case 'difference': {
			return {
				type: 'difference',
				include: normalizeSelection(selection.include),
				exclude: normalizeSelection(selection.exclude),
			};
		}

		default: {
			const unsupportedSelection = selection as {type?: unknown};
			throw new Error(`Unsupported selection type ${String(unsupportedSelection.type)}`);
		}
	}
}

export function toQuery(selection: Selection, context: SelectionLoweringContext = {}): Query {
	return lowerSelection(normalizeSelection(selection), context);
}

export function matches(selection: Selection, document: Types.Document, context: SelectionLoweringContext = {}): boolean {
	return new SelectionMatcher(selection, context).matches(document);
}

export class SelectionMatcher {
	selection: Selection;
	query: Query;
	private readonly _matcher: QueryMatcher;

	constructor(selection: Selection, context: SelectionLoweringContext = {}) {
		this.selection = normalizeSelection(selection);
		this.query = toQuery(this.selection, context);
		this._matcher = new QueryMatcher(this.query);
	}

	matches(document: Types.Document) {
		return this._matcher.match(document);
	}
}

export class DocumentSelection extends EventEmitter {
	public ids: Types.DocumentId[] = [];
	private _ids = new Set<Types.DocumentId>();

	constructor(ids: Types.DocumentId[] = []) {
		super();

		exposeSelectionMethods(this);

		for (const id of ids) {
			addSelectionId(this, id);
		}

		syncSelectionIds(this);
	}

	get size() {
		return this.ids.length;
	}

	get length() {
		return this.ids.length;
	}

	add(ids: Types.DocumentId | Types.DocumentId[]) {
		const added: Types.DocumentId[] = [];

		for (const id of normalizeDocumentIds(ids)) {
			if (addSelectionId(this, id)) {
				added.push(id);
			}
		}

		if (added.length > 0) {
			syncSelectionIds(this);
			emitSelectionChanged(this, added, []);
		}

		return this;
	}

	remove(ids: Types.DocumentId | Types.DocumentId[]) {
		const removed: Types.DocumentId[] = [];

		for (const id of normalizeDocumentIds(ids)) {
			if (this._ids.delete(id)) {
				removed.push(id);
			}
		}

		if (removed.length > 0) {
			syncSelectionIds(this);
			emitSelectionChanged(this, [], removed);
		}

		return this;
	}

	set(ids: Types.DocumentId[]) {
		const next = new Set<Types.DocumentId>();

		for (const id of normalizeDocumentIds(ids)) {
			next.add(id);
		}

		const added = Array.from(next).filter(id => !this._ids.has(id));
		const removed = Array.from(this._ids).filter(id => !next.has(id));

		if (added.length === 0 && removed.length === 0) {
			return this;
		}

		this._ids = next;
		syncSelectionIds(this);
		emitSelectionChanged(this, added, removed);

		return this;
	}

	clear() {
		const removed = [...this.ids];

		if (removed.length === 0) {
			return this;
		}

		this._ids.clear();
		syncSelectionIds(this);
		emitSelectionChanged(this, [], removed);

		return this;
	}

	has(id: Types.DocumentId) {
		return this._ids.has(id);
	}

	matches(document: Types.Document) {
		return this.has(readId(document));
	}

	toArray() {
		return this.ids;
	}

	toSelection(): IdsSelection {
		return ids(this.ids);
	}

	toQuery(context: SelectionLoweringContext = {}) {
		return toQuery(this.toSelection(), context);
	}

	toJSON() {
		return this.ids;
	}

	release() {
		this.removeAllListeners();
		this._ids.clear();
		syncSelectionIds(this);
	}

	[Symbol.iterator]() {
		return this._ids[Symbol.iterator]();
	}

	addListener(event: string | RegExp, listener) {
		return super.addListener(normalizeSelectionEventName(event), listener);
	}

	on(event: string | RegExp, listener) {
		return this.addListener(event, listener);
	}

	once(event: string | RegExp, listener) {
		return super.once(normalizeSelectionEventName(event), listener);
	}

	removeListener(event: string | RegExp, listener) {
		return super.removeListener(normalizeSelectionEventName(event), listener);
	}

	off(event: string | RegExp, listener) {
		return this.removeListener(event, listener);
	}

	removeAllListeners(event?: string | RegExp) {
		if (event === undefined) {
			return super.removeAllListeners();
		}

		return super.removeAllListeners(normalizeSelectionEventName(event));
	}

	emit(event: string | RegExp, ...args: unknown[]) {
		return super.emit(normalizeSelectionEventName(event), ...args);
	}
}

export class SelectionFeature {
	create(ids: Types.DocumentId[] = []) {
		return new DocumentSelection(ids);
	}
}

function lowerSelection(selection: Selection, context: SelectionLoweringContext): Query {
	switch (selection.type) {
		case 'all': {
			return createAllQuery();
		}

		case 'none': {
			return createNoneQuery();
		}

		case 'ids': {
			return createIdsQuery(selection.ids);
		}

		case 'query': {
			assertQuerySupported(selection.query, context);
			return cloneRuntimeQuery(selection.query);
		}

		case 'union': {
			return combineQueries('OR', selection.selections.map(child => lowerSelection(child, context)));
		}

		case 'intersection': {
			return combineQueries('AND', selection.selections.map(child => lowerSelection(child, context)));
		}

		case 'difference': {
			return combineQueries('AND', [
				lowerSelection(selection.include, context),
				negateQuery(lowerSelection(selection.exclude, context), context),
			]);
		}

		default: {
			throw new Error(`Unsupported selection type ${(selection as any).type}`);
		}
	}
}

function createAllQuery(): Query {
	return Scopes.global().toQuery();
}

function createNoneQuery(options?: MatchOptions): Query {
	const parameters: QueryParameters = ['__aeppic_selection_none__'];

	return {
		graph: {
			operator: 'AND',
			conditions: [
				{
					field: 'id',
					term: {id: 0},
				},
				{
					field: 'id',
					term: {id: 0},
					termPrefix: '-',
				},
			],
		},
		parameters,
		options: options ? {...options} : {},
	};
}

function createIdsQuery(ids: Types.DocumentId[]): Query {
	if (ids.length === 0) {
		return createNoneQuery();
	}

	return Scopes.global().where('id').isOneOf(ids).toQuery();
}

function combineQueries(operator: 'AND' | 'OR', queries: Query[]): Query {
	if (queries.length === 0) {
		return operator === 'AND' ? createAllQuery() : createNoneQuery();
	}

	if (operator === 'OR' && queries.some(query => isAllQuery(query))) {
		return createAllQuery();
	}

	const targetParameters: MutableQueryParameters = [];
	const conditions: Condition[] = [];

	for (const query of queries) {
		if (operator === 'AND' && isAllQuery(query)) {
			continue;
		}

		const copiedGraph = copyMatchExpression(query.graph, query.parameters, targetParameters);
		conditions.push(...expressionConditionsForOperator(copiedGraph, operator));
	}

	if (conditions.length === 0) {
		return operator === 'AND' ? createAllQuery() : createNoneQuery();
	}

	const combinedQuery: Query = {
		graph: buildExpression(operator, conditions),
		parameters: targetParameters as QueryParameters,
	};

	const options = mergeQueryOptions(queries);

	if (options) {
		combinedQuery.options = options;
	}

	return combinedQuery;
}

function negateQuery(query: Query, context: SelectionLoweringContext): Query {
	assertQuerySupported(query, context);

	try {
		if (isAllQuery(query)) {
			return createNoneQuery(query.options);
		}

		const negatedQuery: Query = {
			graph: buildExpressionFromCondition(negateCondition(query.graph)),
			parameters: cloneQueryParameters(query.parameters),
		};

		if (query.options) {
			negatedQuery.options = {...query.options};
		}

		return negatedQuery;
	} catch (error) {
		if (context.resolver?.negateQuery) {
			return context.resolver.negateQuery(query, context);
		}

		if (error instanceof Error) {
			throw error;
		}

		throw new Error(String(error));
	}
}

function negateCondition(condition: Condition): Condition {
	if (isFieldMatchNode(condition)) {
		return negateFieldCondition(condition);
	}

	if (condition.conditions.length === 0) {
		return createNoneQuery().graph;
	}

	const operator = ('operator' in condition ? condition.operator : 'AND') as string;

	if (operator !== 'AND' && operator !== 'OR') {
		throw new UnsupportedSelectionFeatureError(`not(${operator})`);
	}

	const negatedConditions = condition.conditions.map(child => negateCondition(child));

	if (negatedConditions.length === 1) {
		return negatedConditions[0];
	}

	return {
		operator: operator === 'OR' ? 'AND' : 'OR',
		conditions: negatedConditions,
	};
}

function negateFieldCondition(condition: FieldMatchNode): FieldMatchNode {
	assertNegatableFieldCondition(condition);

	const negatedCondition: FieldMatchNode = {
		...condition,
		termPrefix: condition.termPrefix === '-' ? undefined : '-',
	};

	if (negatedCondition.termPrefix === undefined) {
		delete negatedCondition.termPrefix;
	}

	return negatedCondition;
}

function assertQuerySupported(query: Query, context: SelectionLoweringContext) {
	if (context.resolver?.assertQuerySupported) {
		context.resolver.assertQuerySupported(query, context);
		return;
	}

	assertRuntimeQueryGraph(query.graph);
}

function assertRuntimeQueryGraph(node: unknown) {
	if (!isObject(node)) {
		throw new UnsupportedSelectionFeatureError('query-graph');
	}

	if (isFieldMatchNode(node)) {
		return;
	}

	if (Array.isArray((node as MatchExpression).conditions)) {
		for (const condition of (node as MatchExpression).conditions) {
			assertRuntimeQueryGraph(condition);
		}

		return;
	}

	if ('type' in node) {
		throw new UnsupportedSelectionFeatureError(`query-graph:${String((node as {type: unknown}).type)}`);
	}

	throw new UnsupportedSelectionFeatureError('query-graph');
}

function assertNegatableFieldCondition(condition: FieldMatchNode) {
	const hasUnsupportedFieldOptions = condition.term_min !== undefined
      || condition.term_max !== undefined
      || condition.termQuotes !== undefined
      || condition.fieldPrefix !== undefined
      || condition.similarity !== undefined
      || condition.boost !== undefined;

	if ((condition.field !== 'id' && condition.field !== 'f.id')
      || condition.term === undefined
      || hasUnsupportedFieldOptions) {
		throw new UnsupportedSelectionFeatureError(`not(where('${condition.field}'))`, 'Selection difference currently only supports excluding id and f.id predicates built with is() or isAnyOf().');
	}
}

function copyMatchExpression(expression: MatchExpression, sourceParameters: QueryParameters, targetParameters: MutableQueryParameters): MatchExpression {
	return copyCondition(expression, sourceParameters, targetParameters) as MatchExpression;
}

function copyCondition(condition: Condition, sourceParameters: QueryParameters, targetParameters: MutableQueryParameters): Condition {
	if (isFieldMatchNode(condition)) {
		return copyFieldCondition(condition, sourceParameters, targetParameters);
	}

	const expression: MatchExpression = {
		conditions: condition.conditions.map(child => copyCondition(child, sourceParameters, targetParameters)),
	};

	if ('operator' in condition) {
		return {
			...expression,
			operator: condition.operator,
		};
	}

	return expression;
}

function copyFieldCondition(condition: FieldMatchNode, sourceParameters: QueryParameters, targetParameters: MutableQueryParameters): FieldMatchNode {
	const copiedCondition: FieldMatchNode = {...condition};

	if (condition.term) {
		copiedCondition.term = copyParameterReference(condition.term, sourceParameters, targetParameters);
	}

	if (condition.term_min) {
		copiedCondition.term_min = copyParameterReference(condition.term_min, sourceParameters, targetParameters);
	}

	if (condition.term_max) {
		copiedCondition.term_max = copyParameterReference(condition.term_max, sourceParameters, targetParameters);
	}

	return copiedCondition;
}

function copyParameterReference(reference: QueryParameterReference, sourceParameters: QueryParameters, targetParameters: MutableQueryParameters): QueryParameterReference {
	const value = readParameterValue(reference, sourceParameters);
	const targetReference = {id: targetParameters.length};
	targetParameters.push(cloneParameterValue(value));
	return targetReference;
}

function readParameterValue(reference: QueryParameterReference, parameters: QueryParameters): unknown {
	if (!parameters || !Object.prototype.hasOwnProperty.call(parameters, reference.id)) {
		throw new Error(`Missing query parameter ${String(reference.id)}`);
	}

	return (parameters as Record<string, unknown>)[String(reference.id)];
}

function cloneQueryParameters(parameters: QueryParameters): QueryParameters {
	if (Array.isArray(parameters)) {
		return parameters.map(value => cloneParameterValue(value)) as QueryParameters;
	}

	const copiedParameters: Record<string, unknown> = {};

	for (const [key, value] of Object.entries(parameters)) {
		copiedParameters[key] = cloneParameterValue(value);
	}

	return copiedParameters as QueryParameters;
}

function cloneParameterValue(value: unknown): unknown {
	if (Array.isArray(value)) {
		const values = value as unknown[];
		return [...values];
	}

	return value;
}

function expressionConditionsForOperator(expression: MatchExpression, operator: 'AND' | 'OR'): Condition[] {
	if (expression.conditions.length === 0) {
		return [];
	}

	if (expression.conditions.length === 1) {
		return [expression.conditions[0]];
	}

	if (expressionOperator(expression) === operator) {
		return expression.conditions;
	}

	return [expression];
}

function buildExpression(operator: 'AND' | 'OR', conditions: Condition[]): MatchExpression {
	if (conditions.length === 1) {
		return buildExpressionFromCondition(conditions[0]);
	}

	return {
		operator,
		conditions,
	};
}

function buildExpressionFromCondition(condition: Condition): MatchExpression {
	if (isFieldMatchNode(condition)) {
		return {conditions: [condition]};
	}

	return condition;
}

function expressionOperator(expression: MatchExpression) {
	return 'operator' in expression ? expression.operator : 'AND';
}

function isAllQuery(query: Query) {
	return query.graph.conditions.length === 0;
}

function mergeQueryOptions(queries: Query[]): MatchOptions | undefined {
	const merged: MatchOptions = {};

	for (const query of queries) {
		if (!query.options) {
			continue;
		}

		for (const [key, value] of Object.entries(query.options)) {
			if (Object.prototype.hasOwnProperty.call(merged, key) && (merged as any)[key] !== value) {
				throw new Error(`Cannot combine selection queries with conflicting match option ${key}`);
			}

			(merged as any)[key] = value;
		}
	}

	return Object.keys(merged).length > 0 ? merged : undefined;
}

function assertSelectionArray(selections: Selection[], type: string) {
	if (!Array.isArray(selections)) {
		throw new TypeError(`${type} selection requires a selections array`);
	}
}

function normalizeDocumentIds(ids: Types.DocumentId | Types.DocumentId[]) {
	const normalized = Array.isArray(ids) ? ids : [ids];
	const uniqueIds = new Set<Types.DocumentId>();

	for (const id of normalized) {
		assertValidSelectionId(id);
		uniqueIds.add(id);
	}

	return [...uniqueIds];
}

function normalizeSelectionEventName(event: string | RegExp) {
	return event === 'change' ? 'changed' : event;
}

function addSelectionId(selection: DocumentSelection, id: Types.DocumentId) {
	assertValidSelectionId(id);

	if (getSelectionIds(selection).has(id)) {
		return false;
	}

	getSelectionIds(selection).add(id);
	return true;
}

function assertValidSelectionId(id: Types.DocumentId) {
	if (typeof id !== 'string' || id.length === 0) {
		throw new Error('Selection document id must be a non-empty string');
	}
}

function syncSelectionIds(selection: DocumentSelection) {
	selection.ids.splice(0, selection.ids.length, ...Array.from(getSelectionIds(selection)));
}

function emitSelectionChanged(selection: DocumentSelection, added: Types.DocumentId[], removed: Types.DocumentId[]) {
	const event: SelectionChangedEvent = {
		selection,
		ids: [...selection.ids],
		added,
		removed,
	};

	selection.emit('changed', event);
}

const exposedDocumentSelectionMethods: Array<keyof DocumentSelection> = [
	'add',
	'remove',
	'set',
	'clear',
	'has',
	'matches',
	'toArray',
	'toSelection',
	'toQuery',
	'toJSON',
	'release',
	'addListener',
	'on',
	'once',
	'removeListener',
	'off',
	'removeAllListeners',
	'emit',
];

function exposeSelectionMethods(selection: DocumentSelection) {
	for (const method of exposedDocumentSelectionMethods) {
		const value: unknown = selection[method];

		if (typeof value !== 'function') {
			continue;
		}

		const callable = value as (this: DocumentSelection, ...args: unknown[]) => unknown;
		const boundMethod = (...args: unknown[]) => callable.call(selection, ...args) as unknown;

		Object.defineProperty(selection, method, {
			value: boundMethod,
			enumerable: true,
			configurable: true,
			writable: true,
		});
	}
}

function getSelectionIds(selection: DocumentSelection) {
	return (selection as unknown as {_ids: Set<Types.DocumentId>})._ids;
}

function isObject(object: unknown): object is Record<string, unknown> {
	return object !== null && object !== undefined && typeof object === 'object';
}
